// src/pages/dashboard.js
// ── CHANGES ──────────────────────────────────────────────────
//   • 24-HOUR GIG WINDOW: a gig appears in the Today or Tomorrow
//     card section as soon as it enters a planning window, and
//     disappears only when the user marks it complete.
//
//     TODAY section  - gigs whose call_time (or show time) falls
//                      on the current calendar day. Visible any
//                      time on that day until marked complete.
//
//     TOMORROW section - gigs whose call_time falls on tomorrow's
//                      calendar day. Visible only AFTER 12:00 PM
//                      today (noon) so the performer can start
//                      planning the night before.
//
//   • completed=true removes a gig from both sections immediately.
//   • checkPendingNotifications() called on mount (no-op but
//     kept for call-site compatibility).
//   • "Upcoming Gigs" stat card shows next-14-days count.
//   • DATE BUG FIX: parts-based local-midnight parsing throughout.
//
//   • HIDE EARNINGS: reads user_metadata.hide_earnings from Supabase.
//     When true, the This Month and Total Earnings stat cards are
//     hidden, and pay amounts are hidden on every gig card in
//     "Gigs This Week". The setting is also passed down to
//     TodaysGigs → TodaysGigCard so the Pay block on the full-screen
//     today card is hidden too.
//
//   • TOMORROW GAP FIX: upcomingGigs now starts from tomorrow when
//     isAfterNoonToday is false, so a tomorrow gig is never orphaned
//     (invisible in both the Tomorrow card and Gigs This Week) before noon.
//
//   • TRIP ROUTING FIX: gigDate={todayStr} is now passed to the Today
//     TodaysGigs block. Without this, TodaysGigCard used activeTripToday
//     instead of getActiveTripForDate(todayStr), causing routing to fall
//     back to the Charlotte home address even when a trip was active.
//
//   • BELL REMOVED FROM HEADER: the notification bell that previously
//     sat next to the Calendar / Add Gig buttons has been removed.
//     The bell now lives in Layout's persistent top bar (main-top-bar)
//     so it is accessible from every page, not just the dashboard.
//     pendingCount local state and its Supabase/realtime effect have
//     been removed accordingly — PendingRequestsContext feeds the
//     Layout bell with the same live count.
//
//   • TRIP ARRIVAL FIX: nextTrip now filters based on arrival dates
//     (arrive_at for multi-leg, arrive_dest_at for round trips) instead
//     of departure dates, so trips appear on the dashboard when the
//     performer arrives at the stay.
//
//   • UPCOMING GIGS TIME ORDER FIX: upcomingGigs previously sorted
//     only by date, so two gigs on the same day kept whatever order
//     they happened to load in rather than earliest-call-time-first.
//     The sort now falls through to call_time/time as a tiebreaker
//     when two gigs land on the same date.
//
//   • AM/PM DISPLAY: the "Gigs This Week" list now runs gig.time
//     through formatTimeAMPM (from FormInputs.jsx) before printing
//     it, so times display as "6:00 PM" instead of the raw 24-hour
//     "18:00" stored in Supabase. Reuses the exact same
//     parseHHMM/to12h conversion TimeInput itself uses, so display
//     formatting can never drift out of sync with the time picker.

import { useState, useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { useGigs } from '../hooks/useGigs'
import { useMusicTracks } from '../hooks/useMusicTracks'
import { useNotificationPrefs } from '../hooks/useNotificationPrefs'
import { useScheduleNotifications } from '../hooks/useScheduleNotifications'
import { useTripMode } from '../hooks/useTripMode'
import { TodaysGigs } from '../components/TodaysGigCard'
import { formatTimeAMPM } from '../components/FormInputs'
import { supabase } from '../lib/supabaseClient'


// ── parseGigDateLocal ──────────────────────────────────────────
// Converts a Supabase date string to a Date at LOCAL midnight.
// new Date("YYYY-MM-DD") parses as UTC midnight, which shifts the
// date back one day in any timezone behind UTC. Passing numeric
// parts to new Date() always uses local time instead.
function parseGigDateLocal(dateStr) {
  const str          = typeof dateStr === 'string' ? dateStr : String(dateStr)
  const [yr, mo, dy] = str.split('T')[0].split('-').map(Number)
  const d = new Date(yr, mo - 1, dy)
  d.setHours(0, 0, 0, 0)
  return d
}


function Dashboard() {

  const { user }          = useAuth()
  // user.user_metadata.performer_name = stage name shown in the greeting

  const { gigs, loading } = useGigs()
  // gigs = full array of this user's gigs loaded from Supabase

  const { tracks: musicTracks = [] }          = useMusicTracks()
  // musicTracks = needed to resolve track IDs in setlists to names for topSongs

  const { prepMinutes, homeLat, homeLng }      = useNotificationPrefs()
  // prepMinutes, homeLat, homeLng passed to TodaysGigs for routing and timing

  const { checkPendingNotifications }          = useScheduleNotifications()
  // no-op now but called on mount to match the vanilla app's dashboard init pattern

  const { trips }                              = useTripMode()
  // trips = saved trip objects, used to calculate the next trip countdown

  // ── PENDING NOTIFICATIONS ON MOUNT ───────────────────────────
  // Mirror the vanilla capstone's dashboard init call.
  // Any notifications the performer missed while the app was closed
  // fire immediately when they open the dashboard.
  useEffect(() => {
    checkPendingNotifications()
  }, [checkPendingNotifications])


  // ── HIDE EARNINGS ─────────────────────────────────────────────
  // Reads user_metadata.hide_earnings that was saved from Settings.
  // When true:
  //   - "This Month" stat card is hidden
  //   - "Total Earnings" stat card is hidden
  //   - $gig.payment is hidden on every "Gigs This Week" card
  //   - The Pay block on TodaysGigCard is hidden (via prop chain)
  // All earnings data remains fully visible on the Earnings page.
  const [hideEarnings, setHideEarnings] = useState(false)

  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (user) setHideEarnings(!!user.user_metadata?.hide_earnings)
      // !! converts undefined/null to false, true stays true
    })
  }, [])


  const motivationalQuotes = useMemo(() => [
    "You Got This!",
    "Slay!",
    "Werk!",
    "Fabulous As Always!",
    "Remember, You Are Fierce!",
    "Own That Stage!",
    "Wash Your Costumes!",
    "Wash Your Brushes!",
    "You're A Star!",
    "Remember to Stretch",
    "You're Doing Great!",
    "Live Your Best Life!",
    "Drink Water!",
    "You Matter",
    "Take Care of Yourself!",
    "Know Your Worth!",
    "You Are Enough",
    "You Are Loved",
    "You Are Important!",
  ], [])
  // useMemo with [] = build this array once and never rebuild it

  const [currentQuote, setCurrentQuote] = useState(() => {
    const randomIndex = Math.floor(Math.random() * motivationalQuotes.length)
    return motivationalQuotes[randomIndex]
    // start on a random quote so it is not always the same one on load
  })

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentQuote(prev => {
        const currentIndex = motivationalQuotes.indexOf(prev)
        const nextIndex    = (currentIndex + 1) % motivationalQuotes.length
        // % wraps back to 0 after the last quote so it loops forever
        return motivationalQuotes[nextIndex]
      })
    }, 5000)
    // rotate every 5 seconds

    return () => clearInterval(interval)
    // clean up when the component unmounts to avoid a memory leak
  }, [motivationalQuotes])


  // ── DERIVED DATA ──────────────────────────────────────────────

  const performerName = user?.user_metadata?.performer_name || "Performer"
  // fall back to "Performer" if the stage name has not been set yet

  const now   = new Date()
  const today = new Date(now)
  today.setHours(0, 0, 0, 0)
  // today at local midnight -- used for all date comparisons below

  const currentMonth = today.getMonth()
  const currentYear  = today.getFullYear()

  const todayStr = `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,'0')}-${String(today.getDate()).padStart(2,'0')}`
  // YYYY-MM-DD string for today built without toISOString() to avoid UTC offset shifting the date

  // ── TODAY section ─────────────────────────────────────────────
  const todaysGigs = gigs
    .filter(gig => {
      if (gig.completed) return false
      // skip gigs already marked as done
      const gigDate = parseGigDateLocal(gig.date)
      return gigDate.getTime() === today.getTime()
      // getTime() compares milliseconds for exact equality
    })
    .sort((a, b) => (a.call_time || a.time || '').localeCompare(b.call_time || b.time || ''))
    // sort by call_time, falling back to show time, so earliest comes first

  // ── TOMORROW section ──────────────────────────────────────────
  const tomorrow = new Date(today)
  tomorrow.setDate(today.getDate() + 1)
  // add one calendar day to get tomorrow at midnight

  const isAfterNoonToday = now.getHours() >= 12
  // true when it is 12:00 PM or later -- gates whether tomorrow shows

  const tomorrowsGigs = isAfterNoonToday
    ? gigs
        .filter(gig => {
          if (gig.completed) return false
          const gigDate = parseGigDateLocal(gig.date)
          return gigDate.getTime() === tomorrow.getTime()
        })
        .sort((a, b) => (a.call_time || a.time || '').localeCompare(b.call_time || b.time || ''))
    : []
    // empty array before noon so the Tomorrow section stays hidden

  const twoWeeksFromNow = new Date(today)
  twoWeeksFromNow.setDate(today.getDate() + 14)
  twoWeeksFromNow.setHours(23, 59, 59, 999)
  // end of day 14 days from now -- used for the "Upcoming Gigs" stat count

  const sevenDaysFromNow = new Date(today)
  sevenDaysFromNow.setDate(today.getDate() + 7)
  sevenDaysFromNow.setHours(23, 59, 59, 999)
  // end of day 7 days from now -- used for the "Gigs This Week" list

  const allUpcomingCount = gigs.filter(gig => {
    const gigDate = parseGigDateLocal(gig.date)
    return gigDate >= today && gigDate <= twoWeeksFromNow
  }).length
  // count shown on the "Upcoming Gigs" stat card (14-day window)

  // ── UPCOMING GIGS LIST ────────────────────────────────────────
  // Excludes today and (when visible) tomorrow so those gigs only
  // appear in their dedicated card sections above.
  const dayAfterTomorrow  = new Date(tomorrow)
  dayAfterTomorrow.setDate(tomorrow.getDate() + 1)
  const upcomingStartDate = isAfterNoonToday ? dayAfterTomorrow : tomorrow
  // before noon: start from tomorrow (Tomorrow section is hidden, include those gigs here)
  // after noon:  start from day after tomorrow (Tomorrow section is showing)

  const upcomingGigs = gigs
    .filter(gig => {
      const gigDate = parseGigDateLocal(gig.date)
      return gigDate >= upcomingStartDate && gigDate <= sevenDaysFromNow
    })
    .sort((a, b) => {
      const dateDiff = parseGigDateLocal(a.date) - parseGigDateLocal(b.date)
      if (dateDiff !== 0) return dateDiff
      // same day -- fall back to call_time, then show time, so gigs
      // on the same date are still ordered earliest-to-latest
      return (a.call_time || a.time || '').localeCompare(b.call_time || b.time || '')
    })
    // sort ascending by date, then by time within a day

  // ── EARNINGS TOTALS ───────────────────────────────────────────
  const thisMonthEarnings = gigs
    .filter(gig => {
      const gigDate = parseGigDateLocal(gig.date)
      return gigDate.getMonth() === currentMonth && gigDate.getFullYear() === currentYear
    })
    .reduce((sum, gig) => sum + (parseFloat(gig.payment) || 0), 0)
    // .reduce sums all matching payments, defaulting to 0 if payment is missing

  const totalEarnings = gigs.reduce(
    (sum, gig) => sum + (parseFloat(gig.payment) || 0), 0
  )
  // total across all gigs ever saved

  // ── DAYS UNTIL NEXT GIG ───────────────────────────────────────
  const nextGig = [...gigs]
    .filter(gig => {
      const gigDate = parseGigDateLocal(gig.date)
      return gigDate >= today && !gig.completed
      // future gigs that have not been marked done
    })
    .sort((a, b) => parseGigDateLocal(a.date) - parseGigDateLocal(b.date))[0] || null
    // sort ascending and take [0] = the soonest upcoming gig

  const daysUntilNextGig = nextGig
    ? Math.round((parseGigDateLocal(nextGig.date) - today) / (1000 * 60 * 60 * 24))
    // millisecond difference divided by ms-per-day
    : null

  // ── MOST PERFORMED SONGS ──────────────────────────────────────
  const songGigCounts = {}
  gigs.forEach(gig => {
    ;(gig.setlist || []).forEach(trackId => {
      songGigCounts[trackId] = (songGigCounts[trackId] || 0) + 1
      // build a { trackId: count } map across all setlists
    })
  })

  const topSongs = Object.entries(songGigCounts)
    .sort(([, a], [, b]) => b - a)
    // sort by count descending -- [, a] destructures the value (ignores the key)
    .slice(0, 5)
    // keep only the top 5
    .map(([trackId, count]) => {
      const track = musicTracks.find(t => t.id === trackId)
      return { trackId, count, name: track?.name || null }
    })
    .filter(s => s.name !== null)
    // drop track IDs whose tracks were deleted from the music library

  // ── DAYS UNTIL NEXT TRIP ──────────────────────────────────────
  const nextTrip = [...(trips || [])]
    .filter(trip => {
      const startStr = trip.trip_type === 'multi_leg'
        ? trip.legs?.[0]?.arrive_at?.split('T')[0]
        : trip.arrive_dest_at?.split('T')[0]
        // get the arrival date (when stay begins) for whichever trip type this is
      return startStr && startStr >= todayStr
    })
    .sort((a, b) => {
      const aStr = a.trip_type === 'multi_leg' ? a.legs?.[0]?.arrive_at : a.arrive_dest_at
      const bStr = b.trip_type === 'multi_leg' ? b.legs?.[0]?.arrive_at : b.arrive_dest_at
      return (aStr || '').localeCompare(bStr || '')
    })[0] || null
    // [0] = the soonest upcoming trip after sorting

  const daysUntilNextTrip = nextTrip
    ? (() => {
        const startStr = nextTrip.trip_type === 'multi_leg'
          ? nextTrip.legs?.[0]?.arrive_at?.split('T')[0]
          : nextTrip.arrive_dest_at?.split('T')[0]
        if (!startStr) return null
        const [y, m, d] = startStr.split('-').map(Number)
        const tripDate = new Date(y, m - 1, d)
        tripDate.setHours(0, 0, 0, 0)
        return Math.round((tripDate - today) / (1000 * 60 * 60 * 24))
      })()
    : null


  if (loading) {
    return (
      <div className="dashboard">
        <p>Loading your dashboard...</p>
      </div>
    )
  }


  return (
    <div className="dashboard">

      {/* ── HEADER ROW ───────────────────────────────────────────
          Bell removed — now handled by Layout's persistent top bar.
          Calendar and Add Gig quick-action buttons remain here.
          ─────────────────────────────────────────────────────── */}
      <div className="dash-header-actions">
        <Link
          to="/calendar"
          className="btn-secondary"
        >
          <i className="bi bi-calendar-fill"></i>
          <span>Calendar</span>
        </Link>
        <Link
          to="/gigs/new"
          className="btn-primary"
        >
          <i className="bi bi-plus-lg"></i>
          <span>Add Gig</span>
        </Link>
      </div>

      <header className="dashboard-header">
        <p className="welcome-text">Welcome back,</p>
        <h1 className="performer-name">{performerName}</h1>
        <p className="motivational-quote">"{currentQuote}"</p>
      </header>

      {/* ── TODAY section ─────────────────────────────────────────
          gigDate={todayStr} ensures TodaysGigCard calls
          getActiveTripForDate(todayStr) instead of activeTripToday,
          so the trip stay address is used as the routing origin.
          ─────────────────────────────────────────────────────── */}
      {todaysGigs.length > 0 && (
        <TodaysGigs
          gigs={todaysGigs}
          musicTracks={musicTracks}
          prepMinutes={prepMinutes}
          homeLat={homeLat}
          homeLng={homeLng}
          sectionLabel="Today"
          hideEarnings={hideEarnings}
          gigDate={todayStr}
        />
      )}

      {/* ── TOMORROW section ──────────────────────────────────────
          Visible only after noon today so performers can start
          planning the evening before.
          ─────────────────────────────────────────────────────── */}
      {tomorrowsGigs.length > 0 && (
        <>
          <div className="tomorrow-header">
            <h2 className="section-title">
              <i className="bi bi-calendar-event dash-tomorrow-icon"></i>
              Tomorrow
            </h2>
            <span className="tomorrow-badge">
              Start planning now
            </span>
          </div>
          <TodaysGigs
            gigs={tomorrowsGigs}
            musicTracks={musicTracks}
            prepMinutes={prepMinutes}
            homeLat={homeLat}
            homeLng={homeLng}
            sectionLabel="Tomorrow"
            gigDate={`${tomorrow.getFullYear()}-${String(tomorrow.getMonth()+1).padStart(2,'0')}-${String(tomorrow.getDate()).padStart(2,'0')}`}
            hideEarnings={hideEarnings}
          />
        </>
      )}

      {/* ── STAT CARDS ────────────────────────────────────────── */}
      <div className="stats-row">

        {daysUntilNextGig !== null && (
          <Link to="/gigs" className="stat-card--link" title={`Next gig: ${nextGig?.name || nextGig?.venue_name}`}>
            <div className="stat-card stat-card--hover">
              <p className="stat-label">
                Next Gig{' '}
                <i className="bi bi-arrow-right-circle dash-stat-arrow"></i>
              </p>
              <p className="stat-value">
                {daysUntilNextGig === 0 ? 'Today!' : daysUntilNextGig === 1 ? 'Tomorrow' : `${daysUntilNextGig}d`}
              </p>
              <p className="stat-card-subtitle--truncate">
                {nextGig?.name || nextGig?.venue_name}
              </p>
            </div>
          </Link>
        )}

        {daysUntilNextTrip !== null && (
          <Link to="/calendar" className="stat-card--link" title={`Next trip: ${nextTrip?.destination_label}`}>
            <div className="stat-card stat-card--hover">
              <p className="stat-label">
                <i className={`bi ${
                  nextTrip?.trip_type === 'multi_leg'       ? 'bi-map-fill'         :
                  nextTrip?.transport_mode === 'flight'      ? 'bi-airplane-fill'    :
                  nextTrip?.transport_mode === 'drive'       ? 'bi-car-front-fill'   :
                  nextTrip?.transport_mode === 'train'       ? 'bi-train-front-fill' :
                  nextTrip?.transport_mode === 'bus'         ? 'bi-bus-front-fill'   :
                  'bi-suitcase-fill'
                } dash-stat-transport-icon`}></i>
                Next Trip{' '}
                <i className="bi bi-arrow-right-circle dash-stat-arrow"></i>
              </p>
              <p className="stat-value">
                {daysUntilNextTrip === 0 ? 'Today!' : daysUntilNextTrip === 1 ? 'Tomorrow' : `${daysUntilNextTrip}d`}
              </p>
              <p className="stat-card-subtitle--truncate">
                {nextTrip?.destination_label}
              </p>
            </div>
          </Link>
        )}

        {!hideEarnings && (
          <div className="stat-card">
            <p className="stat-label">This Month</p>
            <p className="stat-value">${thisMonthEarnings.toLocaleString()}</p>
          </div>
        )}

        {!hideEarnings && (
          <Link to="/earnings" className="stat-card--link" title="View full earnings breakdown">
            <div className="stat-card stat-card--hover">
              <p className="stat-label">
                Total Earnings{' '}
                <i className="bi bi-arrow-right-circle dash-stat-arrow"></i>
              </p>
              <p className="stat-value">${totalEarnings.toLocaleString()}</p>
            </div>
          </Link>
        )}

        <div className="stat-card">
          <p className="stat-label">Upcoming Gigs</p>
          <p className="stat-value">{allUpcomingCount}</p>
          <p className="stat-card-subtitle">next 2 weeks</p>
        </div>

      </div>


      {/* ── TOP SONGS ─────────────────────────────────────────── */}
      {topSongs.length > 0 && (
        <section>
          <div className="dash-section-header">
            <h2 className="section-title">
              <i className="bi bi-music-note-list dash-section-icon"></i>
              Most Performed Songs
            </h2>
            <Link to="/music" className="dash-view-all-link">Music Library</Link>
          </div>

          <div className="top-songs-list">
            {topSongs.map((song, index) => (
              <div
                key={song.trackId}
                className={`song-row${index === 0 ? ' song-row--active' : ''}`}
              >
                <span className={`song-rank${index === 0 ? ' song-rank--active' : ''}`}>
                  {index + 1}
                </span>

                <i
                  className={`bi bi-music-note-beamed${index === 0 ? ' song-icon--active' : ' song-icon'}`}
                ></i>

                <span className="song-name">
                  {song.name}
                </span>

                <span className={`song-count-badge${index === 0 ? ' song-count-badge--active' : ''}`}>
                  {song.count} gig{song.count !== 1 ? 's' : ''}
                </span>
              </div>
            ))}
          </div>
        </section>
      )}


      {/* ── GIGS THIS WEEK ────────────────────────────────────── */}
      <section>

        <div className="dash-section-header">
          <h2 className="section-title">Gigs This Week</h2>
          <Link to="/gigs">View All</Link>
        </div>

        {upcomingGigs.length === 0 ? (
          <div className="gig-week-empty">
            <p>
              No gigs in the next 7 days.
            </p>
            <Link to="/gigs/new" className="btn-primary">
              + Add a Gig
            </Link>
          </div>
        ) : (
          <>
            <div className="gig-list">
              {upcomingGigs.map(gig => {
                const gigDate = parseGigDateLocal(gig.date)
                const dayDiff = Math.round((gigDate - today) / (1000 * 60 * 60 * 24))
                const dayLabel = dayDiff === 0
                  ? ' Today!'
                  : dayDiff === 1
                    ? 'Tomorrow'
                    : gigDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })

                return (
                  <Link
                    key={gig.id}
                    to={`/gigs/${gig.id}`}
                    className="dash-gig-link"
                  >
                    <div className={`gig-card${dayDiff === 0 ? ' gig-card--today' : ''}`}>
                      <div className="gig-card-header">
                        <div className="gig-card-info-row">
                          <h3>{gig.name || gig.venue_name}</h3>
                          <p>{gig.venue_name}</p>
                          <p>{gig.venue_address}</p>
                        </div>
                        <div className="gig-card-right">
                          {!hideEarnings && (
                            <div className="gig-card-payment">
                              ${gig.payment}
                            </div>
                          )}
                          <div className={`gig-card-day-label${dayDiff === 0 ? ' gig-card-day-label--today' : ''}`}>
                            {dayLabel}
                          </div>
                        </div>
                      </div>
                      <div className="gig-card-meta">
                        <span><i className="bi bi-calendar3"></i> {gigDate.toLocaleDateString()}</span>
                        <span><i className="bi bi-clock"></i> {formatTimeAMPM(gig.time)}</span>
                        {gig.setlist && gig.setlist.length > 0 && (
                          <span className="gig-card-setlist-count">
                            <i className="bi bi-music-note-list"></i> {gig.setlist.length} song{gig.setlist.length !== 1 ? 's' : ''} pinned
                          </span>
                        )}
                      </div>
                    </div>
                  </Link>
                )
              })}
            </div>
          </>
        )}

      </section>
    </div>
  )
}

export default Dashboard